/**
 * Yatırım Fizibilite — Tanımlar (R8, Faz F1)
 *
 * İki master-data tablosu için tek sayfa, sekme-driven CRUD ekranı.
 * Anayasa Madde 1/2/3/5/6 ile uyumlu:
 *  - Madde 1: Tablo verisi tamamen API'den beslenir (hardcode liste yok)
 *  - Madde 2: Listele + Ekle + Düzenle + Pasifleştir/Aktifleştir + Sil (5 op)
 *  - Madde 3: `kod` immutable — Düzenle modal'ında disabled+readOnly
 *  - Madde 5: Backend MANAGE_FEASIBILITY enforce eder, frontend UX gating de yapar
 *  - Madde 6: Default soft delete, hard delete `?hard=true` + `usage_count == 0`
 *
 * Backend endpoint'leri (R8):
 *  - GET    /api/fizibilite/yatirim-kategorileri?include_inactive=&with_usage=  (VIEW_FEASIBILITY)
 *  - POST   /api/fizibilite/yatirim-kategorileri                                (MANAGE_FEASIBILITY)
 *  - PATCH  /api/fizibilite/yatirim-kategorileri/{id}                           (kod değişimi → 400)
 *  - DELETE /api/fizibilite/yatirim-kategorileri/{id}         → soft (aktif=false)
 *  - DELETE /api/fizibilite/yatirim-kategorileri/{id}?hard=true → hard (usage=0 koşulu)
 *  - Aynı 4 op /api/fizibilite/yatirim-tipleri için.
 */

import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { colors, radius, styles } from '../../styles/theme';
import useUrlState from '../../hooks/useUrlState';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '13px',
  boxSizing: 'border-box',
};

const labelStyle = { fontSize: 12, color: '#666', display: 'block', marginBottom: 4 };

const EMPTY_FORM = { kod: '', ad: '', sira: 0 };

// Sekme tanımları — endpoint + etiketler tek kaynaktan beslenir.
const TABS = [
  {
    key: 'kategoriler',
    label: 'Kategoriler',
    icon: '🗂️',
    endpoint: 'yatirim-kategorileri',
    tekil: 'kategori',
    tekilBuyuk: 'Kategori',
  },
  {
    key: 'tipler',
    label: 'Tipler',
    icon: '🏷️',
    endpoint: 'yatirim-tipleri',
    tekil: 'tip',
    tekilBuyuk: 'Tip',
  },
];

/**
 * Tek bir master-data tablosu için CRUD bölümü (kategoriler veya tipler).
 * `tab` config'i (endpoint + etiketler) ile parametriktir.
 */
const MasterDataSection = ({ tab, canManage }) => {
  const toast = useToast();
  const base = `${API_URL}/api/fizibilite/${tab.endpoint}`;

  const [kayitlar, setKayitlar] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Yeni kayıt modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM });

  // Düzenle modal
  const [editModal, setEditModal] = useState(null); // { id, kod, ad, sira, aktif }

  // ─── Veri yükleme (sessiz refetch) ─────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${base}?include_inactive=${includeInactive}&with_usage=true`,
        { headers: authHeaders() },
      );
      const data = await res.json().catch(() => []);
      setKayitlar(Array.isArray(data) ? data : []);
    } catch (err) {
      logError(`${tab.label} yüklenemedi:`, err);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive, tab.endpoint]);

  // ─── CRUD: Oluştur ─────────────────────────────────────────────
  const openCreate = () => {
    setCreateForm({ ...EMPTY_FORM });
    setCreateModalOpen(true);
  };

  const submitCreate = async () => {
    const { kod, ad } = createForm;
    if (!kod.trim()) return toast.warning('Kod zorunludur.');
    if (!ad.trim()) return toast.warning('Ad zorunludur.');

    setSaving(true);
    try {
      const payload = {
        kod: kod.trim(),
        ad: ad.trim(),
        sira: parseInt(createForm.sira, 10) || 0,
        aktif: true,
      };
      const res = await fetch(base, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success(`${tab.tekilBuyuk} eklendi`);
      setCreateModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── CRUD: Düzenle ─────────────────────────────────────────────
  const openEdit = (k) => {
    setEditModal({
      id: k.id,
      kod: k.kod,
      ad: k.ad || '',
      sira: k.sira ?? 0,
      aktif: k.aktif !== false,
    });
  };

  const submitEdit = async () => {
    if (!editModal) return;
    if (!editModal.ad.trim()) return toast.warning('Ad zorunludur.');

    setSaving(true);
    try {
      // NOT: `kod` payload'a EKLENMEZ — Anayasa Madde 3 (immutable).
      const payload = {
        ad: editModal.ad.trim(),
        sira: parseInt(editModal.sira, 10) || 0,
        aktif: editModal.aktif,
      };
      const res = await fetch(`${base}/${editModal.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success(`${tab.tekilBuyuk} güncellendi`);
      setEditModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Pasifleştir / Aktifleştir ─────────────────────────────────
  const togglePasif = async (k) => {
    const isAktif = k.aktif !== false;
    if (isAktif) {
      if (!window.confirm(`"${k.ad}" ${tab.tekil}i pasifleştirilsin mi? (geri alınabilir)`))
        return;
      try {
        const res = await fetch(`${base}/${k.id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        toast.success(`${tab.tekilBuyuk} pasifleştirildi`);
        fetchAll();
      } catch (err) {
        toast.error(err.message || 'Pasifleştirilemedi');
      }
    } else {
      // Aktifleştir → PATCH {aktif: true}
      try {
        const res = await fetch(`${base}/${k.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ aktif: true }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${res.status}`);
        }
        toast.success(`${tab.tekilBuyuk} aktifleştirildi`);
        fetchAll();
      } catch (err) {
        toast.error(err.message || 'Aktifleştirilemedi');
      }
    }
  };

  // ─── Hard Delete (Sil) ─────────────────────────────────────────
  const hardDelete = async (k) => {
    const usage = k.usage_count || 0;
    if (usage > 0) {
      toast.warning(
        `Bu ${tab.tekil} ${usage} kayıtta kullanılıyor — kalıcı silinemez. Pasifleştirmeyi deneyin.`,
      );
      return;
    }
    if (
      !window.confirm(
        `"${k.ad}" ${tab.tekil}i KALICI olarak silinecek. Geri alınamaz. Devam?`,
      )
    )
      return;
    try {
      const res = await fetch(`${base}/${k.id}?hard=true`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      toast.success(`${tab.tekilBuyuk} kalıcı olarak silindi`);
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Silinemedi');
    }
  };

  if (loading && kayitlar.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>;
  }

  return (
    <div>
      {/* Üst bar: filtre + yeni butonu */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: colors.textSecondary,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Pasifleri göster
        </label>

        {canManage && (
          <button
            onClick={openCreate}
            style={{
              padding: '10px 18px',
              background: '#c62828',
              color: 'white',
              border: 'none',
              borderRadius: radius.sm,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(198,40,40,0.25)',
            }}
          >
            + Yeni {tab.tekilBuyuk}
          </button>
        )}
      </div>

      {/* Tablo */}
      <div
        style={{
          background: colors.surface,
          borderRadius: radius.md,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        {kayitlar.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{tab.icon}</div>
            <div style={{ fontSize: 15, marginBottom: 4 }}>
              Henüz {tab.label.toLowerCase()} tanımlanmamış.
            </div>
            {canManage && (
              <div style={{ fontSize: 13 }}>
                <strong>+ Yeni {tab.tekilBuyuk}</strong> ile başlayın.
              </div>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Kod</th>
                <th style={{ padding: 10 }}>Ad</th>
                <th style={{ padding: 10 }}>Sıra</th>
                <th style={{ padding: 10 }}>Durum</th>
                <th style={{ padding: 10 }}>Kullanım</th>
                {canManage && <th style={{ padding: 10, textAlign: 'right' }}>İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {kayitlar.map((k) => {
                const usage = k.usage_count || 0;
                const isAktif = k.aktif !== false;
                return (
                  <tr
                    key={k.id}
                    style={{
                      borderBottom: `1px solid ${colors.border}`,
                      opacity: isAktif ? 1 : 0.55,
                      background: isAktif ? 'white' : '#fafafa',
                    }}
                  >
                    <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>
                      {k.kod}
                    </td>
                    <td style={{ padding: 10, fontWeight: 500 }}>{k.ad}</td>
                    <td style={{ padding: 10, color: '#666' }}>{k.sira ?? 0}</td>
                    <td style={{ padding: 10 }}>
                      <span
                        style={{
                          background: isAktif ? '#d4edda' : '#f8d7da',
                          color: isAktif ? '#155724' : '#721c24',
                          padding: '2px 8px',
                          borderRadius: radius.lg,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {isAktif ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td style={{ padding: 10, fontSize: 11 }}>
                      {usage > 0 ? (
                        <span
                          style={{
                            background: '#fff3cd',
                            color: '#856404',
                            padding: '2px 8px',
                            borderRadius: 10,
                          }}
                        >
                          {usage} kayıt
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>kullanılmamış</span>
                      )}
                    </td>
                    {canManage && (
                      <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => openEdit(k)}
                          title="Düzenle"
                          style={{
                            padding: '4px 10px',
                            background: '#0d6efd',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            marginRight: 4,
                          }}
                        >
                          ✏️ Düzenle
                        </button>
                        <button
                          onClick={() => togglePasif(k)}
                          title={isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                          style={{
                            padding: '4px 10px',
                            background: isAktif ? '#ffc107' : '#28a745',
                            color: isAktif ? '#333' : 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            marginRight: 4,
                          }}
                        >
                          {isAktif ? 'Pasifleştir' : 'Aktifleştir'}
                        </button>
                        <button
                          onClick={() => hardDelete(k)}
                          disabled={usage > 0}
                          title={
                            usage > 0
                              ? `Bu ${tab.tekil} ${usage} kayıtta kullanılıyor. Önce o kayıtları silin.`
                              : 'Kalıcı sil'
                          }
                          style={{
                            padding: '4px 10px',
                            background: usage > 0 ? '#ccc' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: usage > 0 ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                          }}
                        >
                          🗑️ Sil
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Yeni Kayıt Modal ─────────────────────────────────────── */}
      {createModalOpen && (
        <div
          onClick={() => !saving && setCreateModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: radius.md,
              padding: 24,
              minWidth: 440,
              maxWidth: 520,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>Yeni {tab.tekilBuyuk}</h3>
            <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>
              <strong>Kod</strong> sonradan değiştirilemez (referans bütünlüğü — Anayasa
              Madde 3).
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Kod*</label>
                <input
                  style={inputStyle}
                  value={createForm.kod}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, kod: e.target.value.toUpperCase() })
                  }
                  placeholder="ORN_KOD"
                  maxLength={50}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelStyle}>Ad*</label>
                <input
                  style={inputStyle}
                  value={createForm.ad}
                  onChange={(e) => setCreateForm({ ...createForm, ad: e.target.value })}
                  placeholder={`${tab.tekilBuyuk} adı`}
                  maxLength={200}
                />
              </div>
              <div>
                <label style={labelStyle}>Sıra</label>
                <input
                  type="text"
                  inputMode="numeric"
                  style={inputStyle}
                  value={createForm.sira}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, sira: e.target.value.replace(/[^0-9]/g, '') })
                  }
                />
              </div>
            </div>

            <div
              style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <button
                onClick={() => setCreateModalOpen(false)}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Vazgeç
              </button>
              <button
                onClick={submitCreate}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: saving ? '#999' : styles.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Düzenle Modal ───────────────────────────────────────── */}
      {editModal && (
        <div
          onClick={() => !saving && setEditModal(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: radius.md,
              padding: 24,
              minWidth: 440,
              maxWidth: 520,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>{tab.tekilBuyuk} Düzenle</h3>
            <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>
              <strong>Kod</strong> kayıt oluşturulduktan sonra değiştirilemez (Anayasa
              Madde 3 — referans bütünlüğü).
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Kod (sabit)</label>
                <input
                  style={{
                    ...inputStyle,
                    background: '#f5f5f5',
                    fontFamily: 'monospace',
                    color: '#555',
                  }}
                  value={editModal.kod}
                  disabled
                  readOnly
                  title="Kod kayıt oluşturulduktan sonra değiştirilemez (Anayasa Madde 3)"
                />
              </div>
              <div>
                <label style={labelStyle}>Ad*</label>
                <input
                  style={inputStyle}
                  value={editModal.ad}
                  onChange={(e) => setEditModal({ ...editModal, ad: e.target.value })}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelStyle}>Sıra</label>
                <input
                  type="text"
                  inputMode="numeric"
                  style={inputStyle}
                  value={editModal.sira}
                  onChange={(e) =>
                    setEditModal({ ...editModal, sira: e.target.value.replace(/[^0-9]/g, '') })
                  }
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label
                  style={{
                    ...labelStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={editModal.aktif}
                    onChange={(e) => setEditModal({ ...editModal, aktif: e.target.checked })}
                  />
                  Aktif
                </label>
              </div>
            </div>

            <div
              style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <button
                onClick={() => setEditModal(null)}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Vazgeç
              </button>
              <button
                onClick={submitEdit}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  background: saving ? '#999' : styles.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FizibiliteMasterDataPage = () => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, PERMISSIONS.VIEW_FEASIBILITY, user?.effectivePermissions);
  const canManage = hasPermission(
    user?.role,
    PERMISSIONS.MANAGE_FEASIBILITY,
    user?.effectivePermissions,
  );

  const [activeTab, setActiveTab] = useUrlState('tab', 'kategoriler');
  const current = TABS.find((t) => t.key === activeTab) || TABS[0];

  if (!canView) {
    return (
      <div>
        <Header title="Yatırım Fizibilite — Tanımlar" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok. (VIEW_FEASIBILITY gerekli)
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Yatırım Fizibilite — Tanımlar" />

      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Bilgi notu */}
        <div
          style={{
            background: '#eef6ff',
            border: '1px solid #b6daff',
            color: '#0b5394',
            padding: '12px 16px',
            borderRadius: radius.md,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Yatırım fizibilite senaryolarında kullanılan <strong>kategori</strong> ve{' '}
          <strong>tip</strong> tanımlarını buradan yönetebilirsiniz. <strong>Kod</strong> kayıt
          sonrası değiştirilemez (Anayasa Madde 3); yazım hatası olursa pasifleştirip yenisini
          ekleyin. Kullanımda olan tanımlar kalıcı silinemez (pasifleştirin).
        </div>

        {/* Sekmeler */}
        <div
          style={{
            display: 'flex',
            gap: 4,
            marginBottom: 16,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          {TABS.map((t) => {
            const aktif = t.key === current.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: aktif ? '3px solid #c62828' : '3px solid transparent',
                  color: aktif ? '#c62828' : colors.textSecondary,
                  fontSize: 14,
                  fontWeight: aktif ? 700 : 500,
                  cursor: 'pointer',
                  marginBottom: -2,
                }}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>

        {/* Aktif sekmenin CRUD bölümü — `key` ile sekme değişince state sıfırlanır */}
        <MasterDataSection key={current.key} tab={current} canManage={canManage} />
      </div>
    </div>
  );
};

export default FizibiliteMasterDataPage;
